<template>
  <div v-if="loading && !result" class="tw-flex tw-justify-center tw-py-8">
    <WdsSpinner :size="24" indeterminate />
  </div>
  <WdsAlert v-else-if="error && !result" error data-test="client-contacts-error">
    {{ $t('Could not load the people') }}
  </WdsAlert>
  <WdsStack v-else direction-column gap-lg>
    <WdsStack
      v-for="section in sections"
      :key="section.segment"
      direction-column
      gap-sm
      :data-test="`client-contacts-${section.segment}`"
    >
      <WdsStack align-center justify-space-between gap-sm>
        <WdsHeading as="h3" type="title4">{{ section.title }}</WdsHeading>
        <WdsButtonGhost
          secondary
          class="noscroll -tw-mx-1"
          :href="clientPeopleUrl(companySupplierId, section.segment)"
          :data-test="`client-contacts-${section.segment}-view-all`"
          @click="openSegment($event, section.segment)"
        >
          <template v-if="section.total > 0">
            {{ $tc('View :count person | View all :count people', section.total) }}
          </template>
          <template v-else>{{ $t('View') }}</template>
        </WdsButtonGhost>
      </WdsStack>
      <ClientContactLabel
        v-for="(row, index) in section.rows"
        :key="row.id"
        sm
        :contact="row.user"
        :note="row.note"
        :data-test="`client-contacts-${section.segment}-${index}`"
      />
      <WdsText
        v-if="section.rows.length === 0"
        secondary
        :data-test="`client-contacts-${section.segment}-empty`"
      >
        {{ section.empty }}
      </WdsText>
    </WdsStack>
  </WdsStack>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useQuery } from '@vue/apollo-composable'
import {
  WdsAlert,
  WdsButtonGhost,
  WdsHeading,
  WdsSpinner,
  WdsStack,
  WdsText,
} from '@worksome/wds-components'
import { CompanyTeamRole } from '@/gql/graphql'
import { $t, $tc } from '@/app-bootstrapping/SetUpInternationalization'
import ClientContactLabel from '@/components/Clients/ClientContactLabel.vue'
import { PeopleSegment } from '@/components/Clients/clientPageTabs'
import { clientPeopleUrl } from '@/components/Clients/People/peopleLinks'
import { ClientDrawerContactsQuery } from '@/components/Clients/graphql/clientDrawerContactsQuery'

const props = defineProps<{ companySupplierId: string }>()

const emit = defineEmits<{ (e: 'openSegment', segment: PeopleSegment): void }>()

const { result, loading, error } = useQuery(
  ClientDrawerContactsQuery,
  () => ({ client: props.companySupplierId, accounts: [currentAccount.globalId] }),
  { clientId: 'publicApi' },
)

// Same order as the client page's People tables, so the drawer shows their first rows.
const sections = computed(() => [
  {
    segment: PeopleSegment.Team,
    title: $t('Your team'),
    empty: $t('No one from your team is assigned to this client yet'),
    total: result.value?.team?.paginatorInfo.total ?? 0,
    rows: (result.value?.team?.data ?? []).map((owner) => ({
      id: owner.id,
      user: owner.user,
      note: undefined,
    })),
  },
  {
    segment: PeopleSegment.Agencies,
    title: $t('Agency recruiters'),
    empty: $t('No staffing agency recruiters are assigned to this client yet'),
    total: result.value?.recruiters?.paginatorInfo.total ?? 0,
    rows: (result.value?.recruiters?.data ?? []).map((recruiter) => ({
      id: recruiter.id,
      user: recruiter.user,
      note: recruiter.owned?.__typename === 'SupplierClient' ? recruiter.owned.supplier.name : undefined,
    })),
    hidden:
      result.value?.relation?.__typename !== 'CompanySupplier' ||
      !result.value.relation.viewerCanManageStaffingAgencies,
  },
  {
    segment: PeopleSegment.Client,
    title: $t('Client team'),
    empty: $t('No team members known for this client yet'),
    total: result.value?.clientTeam?.paginatorInfo.total ?? 0,
    rows: (result.value?.clientTeam?.data ?? []).map((member) => ({
      id: member.id,
      user: member.user,
      note: member.accountRole === CompanyTeamRole.Owner ? $t('Owner') : undefined,
    })),
  },
].filter((section) => !section.hidden))

// The href stays real so new-tab clicks still work; only the plain click is handed to the host page.
function openSegment(event: MouseEvent, segment: PeopleSegment) {
  if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
    return
  }

  event.preventDefault()
  emit('openSegment', segment)
}
</script>
